import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TextInput } from '../../../../../ui/input/text_input/text_input';
import { PreScreenAnswers, PreScreenQuestion } from '../../../../../utils/types/assessment_types';
import { LocationPicker } from './LocationPicker';

interface QualifyingQuestionsProps {
  questions: PreScreenQuestion[];
  currentStep: number;
  onComplete: (answers: PreScreenAnswers[]) => void;
}

type VideoRecordingMode = 'idle' | 'camera' | 'recording' | 'processing';

const VideoAnswerInput: React.FC<{
  questionId: string;
  onCapture: (questionId: string, file: File, previewUrl: string) => void;
}> = ({ questionId, onCapture }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [mode, setMode] = useState<VideoRecordingMode>('idle');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [recordingSecs, setRecordingSecs] = useState(0);

  // Recording timer — starts/stops with mode
  useEffect(() => {
    if (mode === 'recording') {
      setRecordingSecs(0);
      timerRef.current = setInterval(() => setRecordingSecs((s) => s + 1), 1000);
    } else {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    }
    return () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
  }, [mode]);

  const formatRecordingTime = (secs: number) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // Stop and fully release the camera stream
  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  useEffect(() => {
    return () => { stopStream(); };
  }, [stopStream]);

  // Attach stream to the video element whenever we enter camera/recording mode
  useEffect(() => {
    if ((mode === 'camera' || mode === 'recording') && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.muted = true;
      videoRef.current.play().catch(() => {});
    }
  }, [mode]);

  const openCamera = async () => {
    try {
      setCameraError(null);
      if (!streamRef.current) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        streamRef.current = stream;
      }
      setMode('camera');
    } catch {
      setCameraError('Camera access failed. You can upload a video instead.');
    }
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const rec = new MediaRecorder(streamRef.current);
    mediaRecorderRef.current = rec;
    rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      const file = new File([blob], `prequal-${Date.now()}.webm`, { type: 'video/webm' });
      const url = URL.createObjectURL(blob);
      stopStream(); // Release camera before showing playback
      setPreviewUrl(url);
      setMode('idle');
      onCapture(questionId, file, url);
    };
    rec.start();
    setMode('recording');
  };

  const stopRecording = () => {
    // Immediately hide live feed — show spinner instead
    setMode('processing');
    mediaRecorderRef.current?.stop();
    mediaRecorderRef.current = null;
  };

  const handleRecordAgain = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    stopStream();
    openCamera();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    stopStream();
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setMode('idle');
    onCapture(questionId, file, url);
  };

  const showPlayback = !!previewUrl && mode === 'idle';
  const showLiveCamera = mode === 'camera' || mode === 'recording';

  return (
    <div className='space-y-3 max-w-md mx-auto'>
      { /* ── Video viewport ── */ }
      <div className='relative rounded-xl border border-slate-200 bg-slate-950 overflow-hidden aspect-video'>

        { /* Recorded / uploaded playback */ }
        { showPlayback && (
          <video
              key={previewUrl}
              src={previewUrl!}
              controls
              className='w-full h-full object-contain bg-black'
          />
        ) }

        { /* Live camera feed */ }
        { showLiveCamera && (
          <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className='w-full h-full object-cover'
          />
        ) }

        { /* Processing spinner */ }
        { mode === 'processing' && (
          <div className='absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-950'>
            <div className='w-8 h-8 border-2 border-white/20 border-t-white/80 rounded-full animate-spin' />
            <p className='text-xs text-white/50 font-medium'>Processing recording…</p>
          </div>
        ) }

        { /* Idle placeholder */ }
        { mode === 'idle' && !previewUrl && (
          <div className='absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-600'>
            <svg className='w-10 h-10 opacity-40' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={1.5}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z' />
            </svg>
            <p className='text-xs text-slate-500'>Record or upload a video</p>
          </div>
        ) }

        { /* REC badge with live timer */ }
        { mode === 'recording' && (
          <div className='absolute top-2 left-2 flex items-center gap-1.5 bg-red-600/90 backdrop-blur-sm text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow'>
            <div className='w-1.5 h-1.5 rounded-full bg-white animate-pulse' />
            REC
            <span className='font-mono tracking-wider'>{ formatRecordingTime(recordingSecs) }</span>
          </div>
        ) }
      </div>

      { cameraError && <p className='text-xs font-semibold text-amber-600'>{ cameraError }</p> }

      { /* ── Controls ── */ }
      <div className='flex flex-wrap gap-2'>
        { showPlayback ? (
          <>
            <button
                type='button'
                onClick={handleRecordAgain}
                className='px-4 py-2 rounded-lg text-xs font-bold text-white bg-emerald-600'
            >
              Record Again
            </button>
            <button
                type='button'
                onClick={() => fileInputRef.current?.click()}
                className='px-4 py-2 rounded-lg text-xs font-bold border border-slate-200 text-slate-700 bg-white'
            >
              Upload Instead
            </button>
          </>
        ) : mode === 'camera' ? (
          <button
              type='button'
              onClick={startRecording}
              className='flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold text-white bg-red-600'
          >
            <div className='w-2 h-2 rounded-full bg-white animate-pulse' />
            Start Recording
          </button>
        ) : mode === 'recording' ? (
          <button
              type='button'
              onClick={stopRecording}
              className='flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold text-white bg-red-600'
          >
            <div className='w-2.5 h-2.5 rounded-sm bg-white' />
            Stop Recording
          </button>
        ) : mode === 'processing' ? (
          <button type='button' disabled className='px-4 py-2 rounded-lg text-xs font-bold text-white bg-slate-400 cursor-not-allowed'>
            Processing…
          </button>
        ) : (
          <>
            <button
                type='button'
                onClick={openCamera}
                className='px-4 py-2 rounded-lg text-xs font-bold text-white bg-emerald-600'
            >
              Record Video
            </button>
            <button
                type='button'
                onClick={() => fileInputRef.current?.click()}
                className='px-4 py-2 rounded-lg text-xs font-bold border border-slate-200 text-slate-700 bg-white'
            >
              Upload Video
            </button>
          </>
        ) }
        <input ref={fileInputRef} type='file' accept='video/*' className='hidden' onChange={handleFileChange} />
      </div>
    </div>
  );
};

export const QualifyingQuestions: React.FC<QualifyingQuestionsProps> = ({
  questions,
  currentStep,
  onComplete,
}) => {
  const [answers, setAnswers] = useState<PreScreenAnswers[]>([]);

  useEffect(() => {
    onComplete(answers);
  }, [answers, onComplete]);

  const handleChangeAnswer = (
    questionId: string,
    questionType: 'numeric' | 'multiple-choice' | 'location' | 'text' | 'video',
    answer: string | number,
    locationData?: any,
  ) => {
    let transformedAnswer: string | number;
    if (questionType === 'numeric') {
      transformedAnswer = answer === '' ? '' : parseInt(answer as string, 10);
    } else {
      transformedAnswer = answer.toString();
    }

    setAnswers((prevAnswers) => {
      const existingAnswerIndex = prevAnswers.findIndex(
        (ans) => ans.questionId === questionId,
      );
      if (existingAnswerIndex !== -1) {
        const updatedAnswers = [...prevAnswers];
        updatedAnswers[existingAnswerIndex] = {
          questionId,
          answer: transformedAnswer,
          questionType: questionType,
          locationData: locationData,
        } as any;
        return updatedAnswers;
      } else {
        return [
          ...prevAnswers,
          {
            questionId,
            answer: transformedAnswer,
            questionType: questionType,
            locationData: locationData,
          } as any,
        ];
      }
    });
  };

  const handleVideoCapture = (questionId: string, file: File, previewUrl: string) => {
    setAnswers((prevAnswers) => {
      const existingIndex = prevAnswers.findIndex((a) => a.questionId === questionId);
      const entry = { questionId, answer: previewUrl, questionType: 'video' as const, videoFile: file } as any;
      if (existingIndex !== -1) {
        const updated = [...prevAnswers];
        updated[existingIndex] = entry;
        return updated;
      }
      return [...prevAnswers, entry];
    });
  };

  const getAnswerValue = (questionId: string) => {
    const answerObj = answers.find((ans) => ans.questionId === questionId);
    return answerObj ? answerObj.answer : '';
  };

  const currentQuestion = questions[currentStep];

  if (!currentQuestion) return null;

  return (
    <div className='py-1'>
      { /* Smaller, sleeker stepper */ }
      <div className='flex items-center justify-center gap-2 mb-8'>
        { questions.map((_, idx) => (
          <React.Fragment key={idx}>
            <div
                className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-[11px] transition-all duration-500 border-2 ${
                  idx === currentStep
                    ? 'bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-100 scale-110'
                    : idx < currentStep
                      ? 'bg-emerald-100 border-emerald-100 text-emerald-600'
                      : 'bg-white border-gray-200 text-gray-400'
                }`}
            >
              { idx < currentStep ? (
                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={3} d='M5 13l4 4L19 7' />
                </svg>
              ) : (
                idx + 1
              ) }
            </div>
            { idx < questions.length - 1 && (
              <div
                  className={`w-8 h-0.5 transition-all duration-700 ${
                    idx < currentStep ? 'bg-emerald-500' : 'bg-gray-100'
                  }`}
              />
            ) }
          </React.Fragment>
        )) }
      </div>

      { /* Compact Question Content with Smooth Transition */ }
      <div
          className='transition-all duration-500 ease-out animate-in fade-in slide-in-from-bottom-2'
          key={currentStep}
      >
        <div className='mb-6 text-center'>
          <p className='text-[13px] font-medium text-emerald-600 uppercase tracking-wider mb-1'>
            Question { currentStep + 1 } of { questions.length }
          </p>
          <h3 className='text-lg font-bold text-slate-800 tracking-tight leading-snug max-w-[400px] mx-auto'>
            { currentQuestion.question }
          </h3>
        </div>

        <div className='bg-slate-50/50 rounded-2xl border border-slate-100 p-6 shadow-sm'>
          { currentQuestion.questionType === 'multiple-choice' ? (
            <div className='grid grid-cols-1 gap-3 max-w-[340px] mx-auto'>
              { currentQuestion.choices?.map((choice: string, idx: number) => (
                <button
                    key={idx}
                    className={`group relative flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all duration-300 outline-none ${
                      getAnswerValue(currentQuestion.questionId).toString() === choice.toString()
                        ? 'border-emerald-500 bg-white shadow-md shadow-emerald-50'
                        : 'border-white bg-white hover:border-emerald-100'
                    }`}
                    onClick={() =>
                      handleChangeAnswer(currentQuestion.questionId, currentQuestion.questionType, choice)
                    }
                >
                  <div
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all duration-300 shrink-0 ${
                        getAnswerValue(currentQuestion.questionId).toString() === choice.toString()
                          ? 'border-emerald-500 bg-emerald-500'
                          : 'border-slate-200'
                      }`}
                  >
                    { getAnswerValue(currentQuestion.questionId).toString() === choice.toString() && (
                      <div className='w-1.5 h-1.5 rounded-full bg-white shadow-sm' />
                    ) }
                  </div>
                  <span
                      className={`text-sm font-semibold transition-colors ${
                        getAnswerValue(currentQuestion.questionId).toString() === choice.toString()
                          ? 'text-slate-900'
                          : 'text-slate-500 group-hover:text-slate-700'
                      }`}
                  >
                    { choice }
                  </span>
                </button>
              )) }
            </div>
          ) : currentQuestion.questionType === 'location' ? (
            <div className='max-w-[440px] mx-auto py-2'>
              <LocationPicker
                  value={getAnswerValue(currentQuestion.questionId).toString()}
                  onChange={(val, data) => handleChangeAnswer(currentQuestion.questionId, 'location', val, data)}
              />
            </div>
          ) : currentQuestion.questionType === 'text' ? (
            <div className='max-w-[400px] mx-auto py-2'>
              <div className='relative group'>
                <TextInput
                    name={`answer-${currentQuestion.questionId}`}
                    value={getAnswerValue(currentQuestion.questionId).toString()}
                    type='text'
                    className='!bg-transparent !border-b-2 !border-x-0 !border-t-0 !border-slate-200 focus:!border-emerald-500 !rounded-none text-lg font-medium text-slate-700 focus:ring-0 placeholder-slate-200 !h-12 transition-all text-center'
                    placeholder='Type your answer here...'
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleChangeAnswer(currentQuestion.questionId, currentQuestion.questionType, e.target.value)
                    }
                />
              </div>
            </div>
          ) : currentQuestion.questionType === 'video' ? (
            <VideoAnswerInput
                questionId={currentQuestion.questionId}
                onCapture={handleVideoCapture}
            />
          ) : (
            <div className='max-w-[280px] mx-auto py-2'>
              <div className='relative group'>
                <TextInput
                    name={`answer-${currentQuestion.questionId}`}
                    value={getAnswerValue(currentQuestion.questionId).toString()}
                    type='number'
                    className='!bg-transparent !border-b-2 !border-x-0 !border-t-0 !border-slate-200 focus:!border-emerald-500 !rounded-none text-2xl font-bold text-slate-700 focus:ring-0 placeholder-slate-200 !h-14 transition-all text-center'
                    placeholder='0'
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleChangeAnswer(currentQuestion.questionId, 'numeric', e.target.value)
                    }
                />
              </div>
              <p className='text-center mt-3 text-slate-400 text-[11px] font-medium uppercase tracking-widest'>
                Numerical response only
              </p>
            </div>
          ) }
        </div>
      </div>
    </div>
  );
};
